import clsx from 'clsx';
import { ClientBrandConfig } from 'types/brand-config';

import { SEARCH_CONFIG } from '@/components/search/search-config';

import './listings.css';

export type ListingsPageSkeletonProps = {
  className?: string;
  id?: string;
  showQuickSearch?: boolean;
  disableMap?: boolean;
  brand?: ClientBrandConfig;
  showWhereInSearch?: boolean;
};

export const ListingsPageSkeleton: React.FC<ListingsPageSkeletonProps> = ({
  className,
  id,
  showQuickSearch,
  disableMap,
  brand,
  showWhereInSearch,
}) => {
  // Keep skeleton deterministic so it can be server-rendered pre-hydration.
  const showMap = !disableMap && !!brand?.showMap;

  const showPropertyList = brand?.showPropertyListOnSearch;
  const showWhereField =
    showWhereInSearch ??
    (showPropertyList || brand?.showResortOnSearch);
  const shouldAdjustForMobile = showPropertyList || showWhereField;

  return (
    <div
      className={clsx('relative', className)}
      id={id}
      aria-busy="true"
      aria-label="Loading search results"
    >
      {showQuickSearch && (
        <>
          {/* Mobile search bar — in document flow; sticky collapsed bar on scroll */}
          <div
            className={clsx(
              'map-hidden-element lg:hidden',
              SEARCH_CONFIG.mobileListingsShellClass
            )}
          >
            <div className="flex w-full flex-col overflow-hidden rounded-[0.75rem] border border-gray-2">
              <div className="h-[3.375rem] w-full animate-pulse border-b border-gray-2 bg-gray-200" />
              <div
                className={clsx(
                  'h-[3.375rem] w-full animate-pulse bg-gray-200',
                  shouldAdjustForMobile && 'border-b border-gray-2'
                )}
              />
              {shouldAdjustForMobile && (
                <div className="h-[3.375rem] w-full animate-pulse bg-gray-200" />
              )}
            </div>
          </div>
          <div className="listings-search-toolbar sticky top-0 z-20 hidden lg:block lg:z-10">
            {/* Desktop search bar */}
            <div className="hidden h-[82px] w-full flex-row bg-gray-1 lg:flex">
              <div className="mx-auto flex h-full w-full max-w-screen-2xl flex-row items-center justify-between px-4">
                <div className="flex flex-shrink-0 gap-1">
                  <div className="h-[54px] w-[9rem] animate-pulse rounded bg-gray-200 xl:w-[10rem]" />
                  <div className="h-[54px] w-32 animate-pulse rounded bg-gray-200" />
                  {showWhereField && (
                    <div className="h-[54px] w-[8rem] animate-pulse rounded bg-gray-200 xl:w-[10rem]" />
                  )}
                  {showPropertyList && (
                    <div className="h-[54px] w-[12rem] animate-pulse rounded bg-gray-200" />
                  )}
                </div>
                {!disableMap && (
                  <div className="flex h-full flex-shrink-0 items-center gap-2">
                    <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Content area — mirrors ListingsContent layout */}
      <div
        className={clsx(
          'lg:flex lg:flex-row',
          showMap
            ? 'min-h-[98dvh] lg:justify-end'
            : 'mx-auto max-w-screen-2xl justify-center'
        )}
      >
        <div className="listing-cards-outer-container relative">
          {/* Sort bar skeleton — mirrors listing-sort.tsx */}
          <div className="flex w-full">
            <div className="mx-auto flex w-full flex-col bg-white px-[1rem] py-3 md:flex-row md:items-center md:justify-between">
              {/* Mobile: property jump, filters, count */}
              <div className="flex w-full flex-col md:hidden">
                <div className="h-5 w-28 animate-pulse rounded bg-gray-200" />
                <div className="flex w-full flex-wrap items-center justify-between gap-2">
                  <div className="h-5 w-20 animate-pulse rounded bg-gray-200" />
                  <div className="h-8 w-24 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="h-5 w-32 animate-pulse rounded bg-gray-200" />
              </div>
              {/* Desktop: count + property jump */}
              <div className="hidden md:flex md:items-center md:gap-4">
                <div className="h-5 w-32 animate-pulse rounded bg-gray-200" />
                <span className="text-gray-3">|</span>
                <div className="h-5 w-28 animate-pulse rounded bg-gray-200" />
              </div>
              {/* Desktop: sort */}
              <div className="hidden md:flex md:items-center md:justify-end">
                <div className="h-8 w-32 animate-pulse rounded bg-gray-200" />
              </div>
            </div>
          </div>

          {/* Cards grid — uses real listing CSS classes */}
          <div
            className={clsx(
              'listing-cards-container no-scrollbar relative h-full',
              showMap && 'lg:map-margin-right'
            )}
          >
            {Array.from({ length: 24 }).map((_, i) => (
              <div key={i} className="listing-card">
                <div className="image-cover relative">
                  <div className="h-[15rem] w-full animate-pulse rounded-xl bg-gray-200" />
                </div>
                <div className="listing-card-content">
                  <div className="card-title">
                    <div className="h-3 w-[40%] max-w-[6rem] animate-pulse rounded bg-gray-200" />
                    <div className="mt-1.5 h-5 w-[75%] max-w-[14rem] animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="card-content">
                    <div className="flex flex-col gap-[0.25rem]">
                      <div className="h-4 w-[60%] max-w-[10rem] animate-pulse rounded bg-gray-200" />
                      <div className="h-4 w-[45%] max-w-[8rem] animate-pulse rounded bg-gray-200" />
                    </div>
                  </div>
                  <div className="listing-rate">
                    <div className="h-6 w-[40%] max-w-[8rem] animate-pulse rounded bg-gray-200" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map placeholder (desktop) — mirrors Map component dimensions */}
        {showMap && (
          <div className="relative hidden flex-none lg:block">
            <div className="sticky top-[85px]">
              <div className="h-8 w-[33dvw] animate-pulse bg-gray-100" />
              <div className="h-[calc(100dvh-167px)] w-[33dvw] animate-pulse bg-gray-200" />
              <div className="h-10 w-[33dvw] animate-pulse bg-gray-100" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
